import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, UserCheck, Users2, Briefcase, GraduationCap,
  ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';

const staffingServices = [
  {
    icon: UserCheck,
    title: 'Contract Consultants',
    description: 'Experienced Guidewire professionals available for short-term and long-term contract engagements.',
    features: [
      'Certified developers',
      'Configuration specialists',
      'Integration experts',
      'Flexible engagement terms',
    ],
  },
  {
    icon: Users2,
    title: 'Dedicated Delivery Teams',
    description: 'Build complete project teams with all roles needed for successful Guidewire implementations.',
    features: [
      'Architects & leads',
      'Developers & testers',
      'Business analysts',
      'Scrum masters',
    ],
  },
  {
    icon: Briefcase,
    title: 'Project Managers & BAs',
    description: 'Seasoned project managers and business analysts with deep insurance domain expertise.',
    features: [
      'Agile/Waterfall experience',
      'Insurance domain knowledge',
      'Stakeholder management',
      'Requirements analysis',
    ],
  },
  {
    icon: GraduationCap,
    title: 'Training & Enablement',
    description: 'Comprehensive training programs to upskill your team on Guidewire technologies.',
    features: [
      'Custom training programs',
      'Certification prep',
      'Hands-on workshops',
      'Knowledge transfer',
    ],
  },
];

const talentProfiles = [
  { role: 'PolicyCenter Developers', count: '150+', expertise: 'Configuration, Gosu, API' },
  { role: 'ClaimCenter Specialists', count: '100+', expertise: 'Workflows, Automation' },
  { role: 'BillingCenter Experts', count: '75+', expertise: 'Billing, Integrations' },
  { role: 'Integration Architects', count: '50+', expertise: 'REST, Cloud, Messaging' },
  { role: 'QA Engineers', count: '100+', expertise: 'Automation, Performance' },
  { role: 'Project Managers', count: '40+', expertise: 'Agile, Insurance Domain' },
];

const Staffing = () => {
  return (
    <div data-testid="staffing-page">
      <SEO {...pageSEO.staffing} />
      <PageHeader
        label="Talent Solutions"
        title="Guidewire Talent & Staffing Solutions"
        description="Access certified Guidewire professionals for contract, project, or dedicated team engagements."
      />

      {/* Overview Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <span className="text-amber-600 font-semibold">Staffing & Talent</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
                The Right Talent, When You Need It
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Finding qualified Guidewire professionals is challenging. Our talent network 
                includes 500+ certified experts ready to support your projects, from individual 
                consultants to complete delivery teams.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Whether you need to augment your team for a specific phase or build 
                a dedicated offshore center, we provide flexible staffing solutions 
                that scale with your needs.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Discuss Your Staffing Needs
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              {/* Talent Pool */}
              <div className="relative bg-gradient-to-br from-amber-50 to-slate-50 rounded-2xl p-8">
                <h4 className="text-lg font-bold text-slate-900 mb-6 text-center">
                  Our Talent Pool
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {talentProfiles.map((profile, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-xl p-4 shadow-sm"
                    >
                      <div className="text-2xl font-bold text-amber-600 mb-1">
                        {profile.count}
                      </div>
                      <div className="font-semibold text-slate-900 text-sm mb-1">
                        {profile.role}
                      </div>
                      <div className="text-xs text-slate-500">
                        {profile.expertise}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Certified Professionals' },
              { value: '48hrs', label: 'Average Response Time' },
              { value: '95%', label: 'Retention Rate' },
              { value: '150+', label: 'Active Engagements' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
              Staffing Services
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Flexible Engagement Models
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the staffing model that works best for your project and organization.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {staffingServices.map((service, index) => (
              <StaggerItem key={index}>
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Ready to Build Your Guidewire Dream Team?
            </h2>
            <p className="text-lg text-amber-100 max-w-2xl mx-auto mb-8">
              Let us help you find the perfect Guidewire talent to accelerate 
              your implementation and achieve your business goals.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-white text-amber-700 hover:bg-amber-50 h-14 px-8"
              >
                Request Talent
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Staffing;
